import { useRef } from "react";
import { type TextFieldProps } from "@mui/material/TextField";
import type { CustomInputProps } from "@components/Input/Input.types.ts";
import { TextField } from "./Input.styles";

const Input: React.FC<CustomInputProps & TextFieldProps> = ({
  testId,
  slotProps,
  type,
  decimals = 0,
  canBeginWithZero = true,
  onChange,
  ...rest
}) => {
  const isNumber = type === "number";
  const lastValidValue = useRef(String(rest.value ?? rest.defaultValue ?? ""));
  const numberPattern = decimals > 0 ? /^\d*(\.\d*)?$/ : /^\d*$/;

  const isAllowedNumber = (value: string) => {
    if (!numberPattern.test(value)) return false;
    return canBeginWithZero || !/^0\d/.test(value);
  };

  const getNextValue = (input: HTMLInputElement, insertedText: string) => {
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    return input.value.slice(0, start) + insertedText + input.value.slice(end);
  };

  return (
    <TextField
      {...rest}
      type={isNumber ? "text" : type}
      onChange={(event) => {
        if (isNumber && !isAllowedNumber(event.target.value)) {
          event.target.value = String(rest.value ?? lastValidValue.current);
          return;
        }
        lastValidValue.current = event.target.value;
        onChange?.(event);
      }}
      slotProps={{
        ...slotProps,
        htmlInput: (ownerState) => {
          const htmlInputProps =
            typeof slotProps?.htmlInput === "function"
              ? slotProps.htmlInput(ownerState)
              : slotProps?.htmlInput;

          return {
            ...htmlInputProps,
            "data-testid": testId,
            ...(isNumber
              ? {
                  type: "text",
                  inputMode: decimals > 0 ? "decimal" : "numeric",
                  pattern: numberPattern.source,
                  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (
                      event.key.length === 1 &&
                      !event.ctrlKey &&
                      !event.metaKey &&
                      !event.altKey &&
                      !isAllowedNumber(getNextValue(event.currentTarget, event.key))
                    ) {
                      event.preventDefault();
                    }
                    htmlInputProps?.onKeyDown?.(event);
                  },
                  onBeforeInput: (event: React.FormEvent<HTMLInputElement>) => {
                    const insertedText = (event.nativeEvent as InputEvent).data;
                    if (
                      insertedText &&
                      !isAllowedNumber(getNextValue(event.currentTarget, insertedText))
                    ) {
                      event.preventDefault();
                    }
                    htmlInputProps?.onBeforeInput?.(event);
                  },
                  onPaste: (event: React.ClipboardEvent<HTMLInputElement>) => {
                    const pastedText = event.clipboardData.getData("text");
                    if (!isAllowedNumber(getNextValue(event.currentTarget, pastedText))) {
                      event.preventDefault();
                    }
                    htmlInputProps?.onPaste?.(event);
                  },
                  onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                    if (isAllowedNumber(event.target.value)) {
                      htmlInputProps?.onChange?.(event);
                    }
                  },
                }
              : {}),
          };
        },
      }}
    />
  );
};

export default Input;
